/**
 * Header behaviors: desktop mega-menu hover, search popdown management,
 * mobile drawer dialog, cart drawer toggle.
 */
window.WsHeader = class WsHeader extends HTMLElement {
  #desktopQuery = window.matchMedia('(min-width: 1200px)');
  #abort = null;
  #searchController = null;
  #searchDebounce = null;
  #resizeObserver = null;

  connectedCallback() {
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.drawer = this.querySelector('[data-menu-drawer]');

    for (const dropdown of this.querySelectorAll('[data-dropdown]')) {
      dropdown.addEventListener('mouseenter', () => this.#hoverToggle(dropdown, true), { signal });
      dropdown.addEventListener('mouseleave', () => this.#hoverToggle(dropdown, false), { signal });
    }

    this.querySelector('[data-menu-open]')?.addEventListener('click', () => this.drawer?.showModal(), { signal });
    this.querySelector('[data-menu-close]')?.addEventListener('click', () => this.drawer?.close(), { signal });
    this.drawer?.addEventListener('click', (event) => {
      if (event.target === this.drawer) this.drawer.close();
    }, { signal });

    this.querySelector('[data-cart-toggle]')?.addEventListener('click', (event) => {
      const cartDrawer = document.querySelector('cart-drawer');
      if (!cartDrawer) return;
      event.preventDefault();
      cartDrawer.open();
    }, { signal });

    const search = this.querySelector('[data-search]');
    search?.addEventListener('toggle', () => {
      if (search.open) search.querySelector('input[type="search"]')?.focus();
    }, { signal });

    search?.querySelector('[data-search-close]')?.addEventListener('click', () => {
      search.open = false;
    }, { signal });

    // Predictive search: bound per scope so both the desktop overlay and the
    // mobile search bar work with the same handler.
    for (const input of this.querySelectorAll('[data-predictive-input]')) {
      const scope = input.closest('[data-predictive-url]');
      if (!scope) continue;
      input.addEventListener('input', (event) => {
        clearTimeout(this.#searchDebounce);
        this.#searchDebounce = setTimeout(() => this.#predictiveSearch(scope, event.target.value.trim()), 250);
      }, { signal });
    }

    document.addEventListener('click', (event) => this.#closeOpenPanels(event), { signal });
    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') this.#closeOpenPanels();
    }, { signal });

    this.#setupStickyAnnouncement();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#searchController?.abort();
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#searchDebounce);
  }

  /**
   * Keep the announcement bar (a separate section above the header) pinned along
   * with the sticky header. Sets --ws-announcement-h to the bar's height so the
   * header sits just beneath it, and flags <html> so the CSS pins the bar. Only
   * runs when the header is configured sticky.
   */
  #setupStickyAnnouncement() {
    if (!this.hasAttribute('data-sticky')) return;

    const announcement = document.querySelector('.ws-announcement');
    const bar = announcement?.closest('.shopify-section') ?? announcement;
    if (!bar) return;

    const root = document.documentElement;
    root.setAttribute('data-header-sticky', '');

    const measure = () => {
      root.style.setProperty('--ws-announcement-h', `${Math.round(bar.offsetHeight)}px`);
    };
    measure();

    if (typeof ResizeObserver !== 'undefined') {
      this.#resizeObserver = new ResizeObserver(measure);
      this.#resizeObserver.observe(bar);
    }
    window.addEventListener('resize', measure, { signal: this.#abort.signal });
  }

  async #predictiveSearch(search, query) {
    const results = search.querySelector('[data-predictive-results]');
    if (!results) return;

    if (query.length < 2) {
      results.hidden = true;
      results.innerHTML = '';
      return;
    }

    this.#searchController?.abort();
    this.#searchController = new AbortController();

    const url = new URL(search.dataset.predictiveUrl, window.location.origin);
    url.searchParams.set('q', query);
    url.searchParams.set('resources[type]', 'product');
    url.searchParams.set('resources[limit]', '8');
    url.searchParams.set('section_id', 'ws-predictive-search');

    try {
      const response = await fetch(url, { signal: this.#searchController.signal });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);

      const html = await response.text();
      const next = new DOMParser().parseFromString(html, 'text/html').querySelector('.ws-predictive');
      if (next) {
        results.innerHTML = '';
        results.append(next);
        results.hidden = false;
      }
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Predictive search failed:', error);
    }
  }

  #hoverToggle(dropdown, open) {
    if (!this.#desktopQuery.matches) return;
    dropdown.open = open;
  }

  #closeOpenPanels(event) {
    for (const panel of this.querySelectorAll('[data-dropdown][open], [data-search][open]')) {
      if (event && panel.contains(event.target)) continue;
      panel.open = false;
    }

    // Hide the mobile predictive results when interacting outside its scope
    for (const results of this.querySelectorAll('[data-predictive-results]:not([hidden])')) {
      const scope = results.closest('[data-predictive-url]');
      if (event && scope?.contains(event.target)) continue;
      results.hidden = true;
    }
  }
}

if (!customElements.get('ws-header')) customElements.define('ws-header', WsHeader);